import React, { useCallback, useMemo, useState } from "react";
import { useReportContext } from "../report_context";
import {
  SelectionTable,
  Text,
  VirtualTable,
  VirtualTableStyles,
} from "@commonsku/styles";
import EmptyState from "../EmptyState";
import {
  ReportColumnConfig,
  ReportRow,
} from "../columns/config/ReportColumnConfig";
import { Column } from "react-table";
import "./ReportTable.css";
import { rebuildTooltip } from "../../helpers";
import Tooltip from "../../helpers/ReactTooltip";
import styled from "styled-components";
import {
  BaseColumnType,
  REPORT_TOOLTIP_TARGET_ID,
} from "../columns/config/types";
import { useIdentity } from "../../../hooks";
import { getIdentityUtils } from "../../../utils";

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  &&& {
    padding-bottom: 16px;
  }
`;

type RowType = Record<keyof ReportRow, ReportRow[keyof ReportRow]>;

const DEFAULT_REPORT_WRAPPER_CLASSNAME = "default-report-table-wrapper";
const DEFAULT_REPORT_HEADER_CLASSNAME = "default-report-table-header";

const ReportTable = ({
  wrapperClassName = DEFAULT_REPORT_WRAPPER_CLASSNAME,
  headerClassName = DEFAULT_REPORT_HEADER_CLASSNAME,
  EmptyStateComponent = null,
  extraRowFilter = null,
}: {
  wrapperClassName?: string;
  headerClassName?: string;
  EmptyStateComponent?: React.FC;
  extraRowFilter?: (row: RowType) => boolean;
}) => {
  const {
    columns,
    reportConfig: { type, withSelection, intro },
    rowData,
    firstLoaded,
    tableHeight,
    pageSize,
    sortByColumn,
    selectedRows,
    onSelectionChange,
    elementSizeParameters,
  } = useReportContext();
  const [sorted, setSorted] = useState<boolean>(false);
  const identity = useIdentity();
  const identityUtils = getIdentityUtils(identity);

  const hasCapabilities = useCallback((capabilities) => identityUtils.hasCapabilities(capabilities), [identity]);
  const hasUserFlags = useCallback((userFlags) => identityUtils.hasUserFlags(userFlags), [identity]);

  const rowStyles = useCallback(
    ({ row, ...rest }) => {
      const style: React.CSSProperties = {};
      if (firstLoaded) {
        style.height = `${elementSizeParameters.ROW_HEIGHT}px`;
      }

      if (selectedRows.includes(row.original)) {
        style.border = "1px solid var(--color-primary1-60)";
        style.background = "var(--color-primary1-20)";
      }
      return style;
    },
    [firstLoaded, selectedRows, elementSizeParameters.ROW_HEIGHT],
  );

  const rowGroupStyles = useCallback(
    ({ row, style }) =>
      firstLoaded
        ? {
            ...style,
            height: `${elementSizeParameters.ROW_HEIGHT}px`,
            top: `${row.index * elementSizeParameters.ROW_HEIGHT}px`,
            ...(row.index + 1 === pageSize
              ? {}
              : { borderBottom: "1px solid rgba(178, 205, 209, 0.3)" }),
          }
        : null,
    [firstLoaded, pageSize, elementSizeParameters.ROW_HEIGHT],
  );

  const filteredColumns = useMemo(() => {
    return columns.filter((c: ReportColumnConfig<BaseColumnType>) => {
      const hasCaps = hasCapabilities(c.capabilities);
      const hasFlags = hasUserFlags(c.userFlags);
      return (
        (c.capabilities?.length ? hasCaps : true) &&
        (c.userFlags?.length ? hasFlags : true) &&
        (!c.when || c.when(identity))
      );
    });
  }, [columns, hasCapabilities, hasUserFlags, identity]);

  const filteredRowData = useMemo(() => {
    return rowData.filter((row: RowType) => {
      if (extraRowFilter) {
        return extraRowFilter(row);
      }
      return true;
    });
  }, [rowData, extraRowFilter]);

  const tableProps = useMemo(
    () =>
      firstLoaded
        ? {
            columns: filteredColumns as unknown as Column<RowType>[],
            data: filteredRowData,
            height: tableHeight,
            tableHeaderProps: {
              className: `report-table-header ${headerClassName}`,
            },
            itemSize: () => elementSizeParameters.ROW_HEIGHT,
            rowStyles,
            rowGroupStyles,
            useTableProps: {
              autoResetSortBy: false,
              manualSortBy: true,
            },
            onScroll: rebuildTooltip,
            onSortChange: (state) => {
              const { sortBy } = state;
              if (sortBy.length > 0) {
                setSorted(true);
                const [{ id, desc }] = sortBy;
                sortByColumn(id, desc ? "desc" : "asc");
              } else {
                sorted && sortByColumn(null, null);
              }
            },
            selectedRows,
            onSelectionChange,
          }
        : null,
    [
      firstLoaded,
      filteredColumns,
      filteredRowData,
      tableHeight,
      headerClassName,
      rowStyles,
      rowGroupStyles,
      selectedRows,
      onSelectionChange,
      elementSizeParameters.ROW_HEIGHT,
      sortByColumn,
      sorted,
    ],
  );

  const TableComponent = useMemo(
    () => (withSelection ? SelectionTable : VirtualTable),
    [withSelection],
  );

  if (!firstLoaded) {
    if (EmptyStateComponent) {
      return <EmptyStateComponent />;
    }
    return (
      <EmptyState
        reportType={type}
        description={intro.description}
        introUrl={intro.url}
      />
    );
  }

  return rowData.length > 0 ? (
    <>
      <Tooltip
        className="report-table-tooltip"
        id={REPORT_TOOLTIP_TARGET_ID}
        isCapture
        delayHide={100}
        delayUpdate={100}
      />
      <VirtualTableStyles
        className={`report-table-wrapper ${wrapperClassName}`}
        tableHeight={tableHeight}
      >
        {/* @ts-ignore */}
        <TableComponent {...tableProps} />
      </VirtualTableStyles>
    </>
  ) : (
    <NoResults>
      <Text>No results found</Text>
    </NoResults>
  );
};

export default ReportTable;
